import { useEffect, useMemo, useState, useCallback } from "react";
import { api, formatApiError, formatINRShort } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { downloadCSV, inr, Kpi, EmptyRow } from "./utils";

export default function ConsumptionVsPurchaseReport() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [period, setPeriod] = useState("month");
  const [months, setMonths] = useState(6);

  const load = () => {
    setBusy(true);
    api.get("/reports/inventory/consumption", { params: { period, months } })
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setBusy(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [period, months]);

  const rows = data?.rows || [];
  const totals = data?.totals || {};

  const exportCSV = () => downloadCSV(
    `consumption-vs-purchase-${period}`,
    ["Period", "Purchase Qty", "Consumption Qty", "Net Qty", "Purchase Value (₹)", "Consumption Value (₹)", "Net Value (₹)"],
    rows.map((r) => [r.period, r.purchase_qty, r.consumption_qty, r.net_qty, r.purchase_value, r.consumption_value, r.net_value])
  );

  // Compute max for bar visualisation
  const maxVal = Math.max(1, ...rows.map((r) => Math.max(r.purchase_qty, r.consumption_qty)));

  return (
    <div>
      <div className="border border-neutral-200 bg-white p-4 mb-4 flex flex-wrap items-end gap-3">
        <div>
          <label className="label-caps block mb-1">Grouping</label>
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger data-testid="cvp-period" className="rounded-sm w-32"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="day">Daily</SelectItem>
              <SelectItem value="month">Monthly</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="label-caps block mb-1">Window (months)</label>
          <Select value={String(months)} onValueChange={(v) => setMonths(Number(v))}>
            <SelectTrigger data-testid="cvp-months" className="rounded-sm w-32"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="1">1</SelectItem>
              <SelectItem value="3">3</SelectItem>
              <SelectItem value="6">6</SelectItem>
              <SelectItem value="12">12</SelectItem>
              <SelectItem value="24">24</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button data-testid="cvp-refresh" onClick={load} disabled={busy} variant="outline" className="rounded-sm">
          {busy ? "Loading…" : "Refresh"}
        </Button>
        <Button data-testid="cvp-export" onClick={exportCSV} className="rounded-sm bg-[#FF5A00] hover:bg-[#e04f00]">
          Download .csv
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <Kpi label="Total purchase qty" value={inr(totals.purchase_qty || 0)} tone="ok" />
        <Kpi label="Total consumption qty" value={inr(totals.consumption_qty || 0)} tone="warn" />
        <Kpi label="Total purchase value" value={formatINRShort(totals.purchase_value || 0)} />
        <Kpi label="Total consumption value" value={formatINRShort(totals.consumption_value || 0)} />
      </div>

      <div className="border border-neutral-200 bg-white overflow-x-auto">
        <table className="w-full text-sm" data-testid="cvp-table">
          <thead className="bg-neutral-50 label-caps">
            <tr>
              <th className="text-left px-4 py-2.5">Period</th>
              <th className="text-right px-4 py-2.5">Purchase Qty</th>
              <th className="text-right px-4 py-2.5">Consumption Qty</th>
              <th className="text-right px-4 py-2.5">Net Qty</th>
              <th className="text-right px-4 py-2.5">Purchase Value</th>
              <th className="text-right px-4 py-2.5">Consumption Value</th>
              <th className="text-right px-4 py-2.5">Net Value</th>
              <th className="text-left px-4 py-2.5 w-64">Trend</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <EmptyRow cols={8} msg="No stock movements in this window." />}
            {rows.map((r) => (
              <tr key={r.period} className="border-t border-neutral-100 data-row" data-testid={`cvp-row-${r.period}`}>
                <td className="px-4 py-2.5 font-mono-num text-xs">{r.period}</td>
                <td className="px-4 py-2.5 text-right font-mono-num text-emerald-700">{inr(r.purchase_qty)}</td>
                <td className="px-4 py-2.5 text-right font-mono-num text-amber-700">{inr(r.consumption_qty)}</td>
                <td className={`px-4 py-2.5 text-right font-mono-num font-semibold ${r.net_qty < 0 ? "text-rose-700" : "text-neutral-800"}`}>{inr(r.net_qty)}</td>
                <td className="px-4 py-2.5 text-right font-mono-num text-emerald-700">₹{inr(r.purchase_value)}</td>
                <td className="px-4 py-2.5 text-right font-mono-num text-amber-700">₹{inr(r.consumption_value)}</td>
                <td className={`px-4 py-2.5 text-right font-mono-num font-semibold ${r.net_value < 0 ? "text-rose-700" : "text-neutral-800"}`}>₹{inr(r.net_value)}</td>
                <td className="px-4 py-2.5">
                  <div className="flex flex-col gap-1">
                    <div className="h-1.5 bg-neutral-100">
                      <div className="h-full bg-emerald-500" style={{ width: `${(r.purchase_qty / maxVal) * 100}%` }} title={`Purchase ${r.purchase_qty}`}></div>
                    </div>
                    <div className="h-1.5 bg-neutral-100">
                      <div className="h-full bg-amber-500" style={{ width: `${(r.consumption_qty / maxVal) * 100}%` }} title={`Consumption ${r.consumption_qty}`}></div>
                    </div>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="mt-3 text-xs text-neutral-500">
        <span className="inline-block w-3 h-2 bg-emerald-500 align-middle mr-1"></span> Purchase &nbsp;
        <span className="inline-block w-3 h-2 bg-amber-500 align-middle mr-1"></span> Consumption
      </p>
    </div>
  );
}

// ============================================================================
// 4. Low / High Stock Alerts
// ============================================================================
